// Availability — weekly recurring windows per weekday (max 4 each) + notes.
// Guides scheduling; it doesn't block anything (approved time off does).
import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../auth/AuthProvider'
import { fetchAvailability } from '../../api/reads'
import { api } from '../../api/mutations'
import { useToast } from '../../components/Toast'
import { ErrorBanner, Spinner } from '../../components/ui'
import { WEEKDAYS, type Weekday } from '../../lib/types'

const MAX_WINDOWS = 4

const DAY_LABELS: Record<Weekday, string> = {
  sun: 'Sunday',
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
}

type Window = { start: string; end: string }
type Weekly = Record<Weekday, Window[]>

function emptyWeekly(): Weekly {
  return { sun: [], mon: [], tue: [], wed: [], thu: [], fri: [], sat: [] }
}

export default function AvailabilityPage() {
  const { email } = useAuth()
  if (!email) return null
  return <AvailabilityEditor email={email} />
}

function AvailabilityEditor({ email }: { email: string }) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const availabilityQuery = useQuery({
    queryKey: ['availability', email],
    queryFn: () => fetchAvailability(email),
  })

  const [weekly, setWeekly] = useState<Weekly>(emptyWeekly)
  const [notes, setNotes] = useState('')
  const [hydrated, setHydrated] = useState(false)
  const [saving, setSaving] = useState(false)

  // Hydrate the editor once from the fetched doc (null doc = all empty).
  useEffect(() => {
    if (hydrated || availabilityQuery.data === undefined) return
    const doc = availabilityQuery.data
    if (doc) {
      const next = emptyWeekly()
      for (const day of WEEKDAYS) {
        next[day] = (doc.weekly[day] ?? []).map((w) => ({ start: w.start, end: w.end }))
      }
      setWeekly(next)
      setNotes(doc.notes ?? '')
    }
    setHydrated(true)
  }, [hydrated, availabilityQuery.data])

  if (availabilityQuery.isPending || !hydrated) return <Spinner label="Loading your availability…" />
  if (availabilityQuery.isError) return <ErrorBanner message="Couldn't load your availability. Try again." />

  const updateWindow = (day: Weekday, index: number, field: keyof Window, value: string) => {
    setWeekly((prev) => ({
      ...prev,
      [day]: prev[day].map((w, i) => (i === index ? { ...w, [field]: value } : w)),
    }))
  }
  const addWindow = (day: Weekday) => {
    setWeekly((prev) =>
      prev[day].length >= MAX_WINDOWS ? prev : { ...prev, [day]: [...prev[day], { start: '', end: '' }] },
    )
  }
  const removeWindow = (day: Weekday, index: number) => {
    setWeekly((prev) => ({ ...prev, [day]: prev[day].filter((_, i) => i !== index) }))
  }

  const save = async () => {
    // Validate: every window fully filled, start before end.
    for (const day of WEEKDAYS) {
      for (const w of weekly[day]) {
        if (!w.start || !w.end) {
          toast(`Fill in both times for ${DAY_LABELS[day]}, or remove the window.`, 'error')
          return
        }
        if (w.start >= w.end) {
          toast(`${DAY_LABELS[day]}: the start time must be before the end time.`, 'error')
          return
        }
      }
    }
    setSaving(true)
    try {
      await api.setAvailability({ weekly, notes: notes.trim() || null })
      await queryClient.invalidateQueries({ queryKey: ['availability', email] })
      toast('Availability saved.', 'success')
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not save your availability.', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div>
        <h1 className="text-lg font-bold text-interlochen">Availability</h1>
        <p className="text-sm text-stone">Availability guides scheduling — approved time off blocks it.</p>
      </div>

      <div className="space-y-3">
        {WEEKDAYS.map((day) => (
          <section key={day} className="card" aria-label={DAY_LABELS[day]}>
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-ink">{DAY_LABELS[day]}</h2>
              {weekly[day].length === 0 && <span className="text-sm text-stone">Any time</span>}
            </div>
            {weekly[day].length > 0 && (
              <ul className="mt-2 space-y-2">
                {weekly[day].map((w, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <label className="sr-only" htmlFor={`${day}-${i}-start`}>
                      {DAY_LABELS[day]} window {i + 1} start
                    </label>
                    <input
                      id={`${day}-${i}-start`}
                      type="time"
                      className="input !w-auto flex-1"
                      value={w.start}
                      onChange={(e) => updateWindow(day, i, 'start', e.target.value)}
                    />
                    <span className="text-stone" aria-hidden>
                      –
                    </span>
                    <label className="sr-only" htmlFor={`${day}-${i}-end`}>
                      {DAY_LABELS[day]} window {i + 1} end
                    </label>
                    <input
                      id={`${day}-${i}-end`}
                      type="time"
                      className="input !w-auto flex-1"
                      value={w.end}
                      onChange={(e) => updateWindow(day, i, 'end', e.target.value)}
                    />
                    <button
                      className="btn-ghost !min-h-[40px] px-2 text-danger"
                      onClick={() => removeWindow(day, i)}
                      aria-label={`Remove ${DAY_LABELS[day]} window ${i + 1}`}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {weekly[day].length < MAX_WINDOWS && (
              <button className="btn-ghost !min-h-[36px] mt-2 px-2 text-sm" onClick={() => addWindow(day)}>
                + Add window
              </button>
            )}
          </section>
        ))}
      </div>

      <div className="card">
        <label htmlFor="availability-notes" className="section-label mb-1 block">
          Notes for your scheduler
        </label>
        <textarea
          id="availability-notes"
          className="input min-h-[80px]"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g., class Tuesday evenings until mid-October"
        />
      </div>

      <button className="btn-primary w-full sm:w-auto" onClick={() => void save()} disabled={saving}>
        {saving ? 'Saving…' : 'Save availability'}
      </button>
    </div>
  )
}
